import SectionLabel from '../ui/SectionLabel';
import SectionTitle from '../ui/SectionTitle';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useAppContext } from '../../context/AppContext';
import { LandingTrans, PriceTrans } from '../../utils/translations';
import { useOnlinePrices } from '../../hooks/useOnlinePrices';

export default function PricingSection() {
  const { language } = useAppContext();
  const t = LandingTrans[language];
  const [ref, visible] = useScrollReveal();
  const { club } = useOnlinePrices();
  const priceT = PriceTrans[language] || PriceTrans.english;
  // EUR is display-only; the note shows what is actually charged
  const clubAmount = club.eur ? `≈ ${club.eur}€` : `${club.ils}₪ / ${club.rub}₽`;
  const clubNote = club.eur ? `${priceT.paidAs} ${club.ils}₪ ${priceT.or} ${club.rub}₽` : null;

  const pricingCards = [
    { badgeKey: 'badgeTrial', nameKey: 'trialName', descKey: 'trialDesc', amount: '50₪', unit: '', subscriptionAmount: null, highlight: false, anchor: false },
    { badgeKey: 'badgeOne', nameKey: 'oneName', descKey: 'oneDesc', amount: '70₪', unit: '', subscriptionAmount: null, highlight: false, anchor: false },
    { badgeKey: 'badgeClub', nameKey: 'clubName', descKey: 'clubDesc', amount: clubAmount, note: clubNote, unit: language === 'russian' ? '/мес' : '/mo', subscriptionAmount: null, highlight: false, anchor: false },
    { badgeKey: 'badgeFour', nameKey: 'fourName', descKey: 'fourDesc', amount: '250₪', unit: '', subscriptionAmount: '230₪', highlight: false, anchor: false },
    { badgeKey: 'badgeEight', nameKey: 'eightName', descKey: 'eightDesc', amount: '350₪', unit: '', subscriptionAmount: '320₪', highlight: true, anchor: false },
    { badgeKey: 'badgeLimitless', nameKey: 'limitlessName', descKey: 'limitlessDesc', amount: '400₪', unit: '', subscriptionAmount: '380₪', highlight: false, anchor: true },
  ];

  return (
    <section
      ref={ref}
      className={`bg-warm-white py-16 md:py-30 px-6 md:px-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div className="max-w-6xl mx-auto">
        <SectionLabel>{t.pricesLabel}</SectionLabel>
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mt-4 mb-10">
          <SectionTitle>
            {t.pricesTitle}<em className="italic text-clay">{t.pricesTitleEm}</em>{t.pricesTitleSuffix}
          </SectionTitle>
          <p className="text-sm font-sans font-light text-ink-light max-w-xs text-left">
            {t.pricesNote}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-sand-dark rounded-sm overflow-hidden">
          {pricingCards.map((card, idx) => (
            <div
              key={idx}
              className={`p-9 flex flex-col items-center text-center ${
                card.anchor
                  ? 'bg-brown hover:bg-clay-dark text-white'
                  : card.highlight
                  ? 'bg-clay hover:bg-clay-dark text-white'
                  : 'bg-warm-white hover:bg-cream'
              } transition-colors`}
            >
              <div className="mb-4">
                <span
                  className={`inline-block text-[10px] font-sans uppercase tracking-widest ${
                    card.anchor ? 'bg-clay text-white px-2 py-1 rounded' : card.highlight ? 'bg-white/20 text-white' : 'text-ink-light'
                  }`}
                >
                  {t[card.badgeKey]}
                </span>
              </div>
              <h3
                className={`font-serif text-xl md:text-[22px] font-medium mb-2 w-full text-center ${
                  card.anchor || card.highlight ? 'text-white' : 'text-ink'
                }`}
              >
                {t[card.nameKey]}
              </h3>
              <p
                className={`text-[13px] font-sans font-light mb-6 w-full max-w-xs ${
                  card.anchor || card.highlight ? 'text-white/80' : 'text-ink-light'
                }`}
              >
                {t[card.descKey]}
              </p>
              <p className="font-serif text-4xl md:text-5xl font-normal">
                <span className={card.anchor ? 'text-clay-light' : card.highlight ? 'text-white' : 'text-clay'}>
                  {card.amount}
                </span>
                {card.subscriptionAmount && (
                  <span className={`text-2xl md:text-3xl ml-1 ${card.anchor ? 'text-white/90' : card.highlight ? 'text-white/90' : 'text-clay'}`}>
                    {' '}({card.subscriptionAmount})
                  </span>
                )}
                <span className={`text-lg ${card.anchor ? 'text-white/40' : card.highlight ? 'text-white/80' : 'text-ink-light'}`}>
                  {card.unit}
                </span>
              </p>
              {card.subscriptionAmount && (
                <p className={`text-[11px] font-sans font-light mt-1 ${card.anchor || card.highlight ? 'text-white' : 'text-ink-light'}`}>
                  {t.perSubscription}
                </p>
              )}
              {card.note && (
                <p className={`text-[11px] font-sans font-light mt-1 ${card.anchor || card.highlight ? 'text-white' : 'text-ink-light'}`}>
                  {card.note}
                </p>
              )}
            </div>
          ))}
        </div>

        <p className="text-[13px] font-sans font-light text-ink-light text-center mt-8 max-w-2xl mx-auto">
          {t.pricingFooter}
          <a href="https://wa.me/972559116975" className="text-clay hover:underline">
            +972 55 911 69 75
          </a>
        </p>
      </div>
    </section>
  );
}
